import { useEffect, useState } from "react";

import {
  emptyFeatureScopes,
  FEATURE_IDS,
  FEATURE_LABELS,
  FeatureId,
  FeatureLevel,
  FeatureScopes,
  scopesFromRecord,
} from "../../auth/featureScopes";
import useCustomerRoles from "../../data/useCustomerRoles";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../notifications/useNotifications";
import Modal from "../../layouts/Modal";
import { ContactType } from "../../models/Contact";
import { ICustomerRole } from "../../models/customerRole/ICustomerRole";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import PopOutBox from "../shared/PopOutBox";

interface RoleModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  role: ICustomerRole | undefined;
}

const LEVEL_OPTIONS: { value: FeatureLevel; label: string }[] = [
  { value: 0, label: "None" },
  { value: 1, label: "Read" },
  { value: 2, label: "Write" },
];

const BASE_TYPES: { value: ContactType; label: string }[] = [
  { value: ContactType.Admin, label: "Admin" },
  { value: ContactType.Staff, label: "Staff" },
  { value: ContactType.Mechanic, label: "Mechanic" },
  { value: ContactType.Driver, label: "Driver" },
  { value: ContactType.Exporter, label: "Exporter" },
];

export default function RoleModal({ open, setOpen, role }: RoleModalProps) {
  const { create, update, remove } = useCustomerRoles();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Role");
  const { addNotification } = useNotification();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [contactType, setContactType] = useState<ContactType>(ContactType.Staff);
  const [featureScopes, setFeatureScopes] = useState<FeatureScopes>(() =>
    emptyFeatureScopes(0)
  );
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Admin / Driver / Exporter drive special frontend nav and page behaviour —
  // customers can edit feature levels but not remove or change the base type.
  const isProtectedBuiltIn =
    !!role?.isSystem &&
    (role.contactType === ContactType.Admin ||
      role.contactType === ContactType.Driver ||
      role.contactType === ContactType.Exporter);

  useEffect(() => {
    if (!open) return;

    if (role) {
      setName(role.name ?? "");
      setDescription(role.description ?? "");
      setContactType(role.contactType as ContactType);
      setFeatureScopes(scopesFromRecord(role.featureScopes));
    } else {
      setName("");
      setDescription("");
      setContactType(ContactType.Staff);
      setFeatureScopes({
        ...emptyFeatureScopes(0),
        dashboard: 2,
        chat: 2,
        leads: 2,
        accounts: 2,
        vehicles: 2,
        parts: 2,
        orderManagement: 2,
        widget: 2,
      });
    }
  }, [role, open]);

  const updateScope = (feature: FeatureId, level: FeatureLevel) => {
    setFeatureScopes((prev) => ({ ...prev, [feature]: level }));
  };

  const onSave = () => {
    if (!name.trim()) return;

    const body = {
      name: name.trim(),
      description: description.trim(),
      contactType,
      featureScopes: FEATURE_IDS.reduce((acc, id) => {
        acc[id] = featureScopes[id];
        return acc;
      }, {} as Record<string, number>),
    };

    if (role) {
      update.mutate(
        { id: role.id, body },
        {
          onSuccess: (res) => {
            saveHandlers.onSuccess();
            if (res?.message) {
              addNotification({
                variant: "success",
                primaryText: res.message,
              });
            }
            setOpen(false);
          },
          onError: saveHandlers.onError,
        }
      );
    } else {
      create.mutate(body, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
        },
        onError: saveHandlers.onError,
      });
    }
  };

  const handleDelete = () => {
    if (!role) return;
    remove.mutate(role.id, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        setOpen(false);
      },
      onError: deleteHandlers.onError,
    });
  };

  return (
    <>
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        title="Delete role"
        message="Are you sure you want to delete this role? Staff currently assigned to it must be reassigned first."
        confirmButtonText="Delete"
        onConfirm={handleDelete}
        isLoading={remove.isLoading}
      />
      <Modal width="max-w-3xl" open={open} setOpen={setOpen}>
        <h1 className="text-xl">{`${role ? "Edit" : "Add"} Role`}</h1>
        <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-gray-500">Name</label>
              <input
                className="w-full mt-1 text-sm text-gray-900 h-[40px]"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Yard Operative"
              />
            </div>
            <div>
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-500">
                  Base type
                </label>
                <PopOutBox position="left" classes="w-72">
                  <div className="p-2 text-xs text-gray-600 space-y-1.5">
                    <p className="font-medium text-gray-900">
                      What does the base type do?
                    </p>
                    <p>
                      It decides which experience the user logs into and how
                      the rest of Wilma identifies them. Feature access below
                      is separate.
                    </p>
                    <ul className="space-y-1">
                      <li>
                        <span className="font-medium">Staff</span> — the
                        standard dashboard experience.
                      </li>
                      <li>
                        <span className="font-medium">Mechanic</span> — same
                        dashboard as staff, but appears in job assignment
                        lists and dismantling targets.
                      </li>
                      <li>
                        <span className="font-medium">Admin</span> — full
                        dashboard access, ignores feature limits.
                      </li>
                      <li>
                        <span className="font-medium">Driver</span> — the
                        dedicated driver experience (Find Lead).
                      </li>
                      <li>
                        <span className="font-medium">Exporter</span> — the
                        dedicated exports experience.
                      </li>
                    </ul>
                  </div>
                </PopOutBox>
              </div>
              <select
                aria-label="Base contact type"
                value={contactType}
                disabled={isProtectedBuiltIn}
                onChange={(e) => setContactType(parseInt(e.target.value))}
                className="w-full mt-1 text-sm text-gray-900 h-[40px] disabled:bg-gray-100 disabled:text-gray-500"
              >
                {BASE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                {isProtectedBuiltIn
                  ? "Built-in Admin, Driver and Exporter keep a fixed base type for their dedicated experience."
                  : "Controls login area (staff / driver / exporter / admin)."}
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-gray-500">
                Description
              </label>
              <input
                className="w-full mt-1 text-sm text-gray-900 h-[40px]"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Feature access
            </label>
            <p className="mt-1 text-xs text-gray-400">
              None hides the area. Read and Write are stored for later API
              enforcement; both allow page access today.
            </p>
            <div className="mt-3 overflow-hidden rounded border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      Feature
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      Access
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {FEATURE_IDS.map((feature) => (
                    <tr key={feature}>
                      <td className="px-3 py-2 text-gray-900">
                        {FEATURE_LABELS[feature]}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          aria-label={`${FEATURE_LABELS[feature]} access`}
                          value={featureScopes[feature]}
                          onChange={(e) =>
                            updateScope(
                              feature,
                              Number(e.target.value) as FeatureLevel
                            )
                          }
                          className="w-full max-w-[10rem] text-sm text-gray-900 h-[36px]"
                        >
                          {LEVEL_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ButtonInput
              label="Save"
              isSubmit={false}
              onClick={onSave}
              classes={undefined}
            />
            {role !== undefined && !isProtectedBuiltIn && (
              <ButtonInput
                label="Delete"
                isSubmit={false}
                onClick={() => setDeleteOpen(true)}
                classes="!bg-red-500 hover:!bg-red-600"
              />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
